import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-quill/dist/quill.snow.css";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://app.akiya2.com/news";

const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const options = { year: "numeric", month: "short", day: "2-digit" };
    return date.toLocaleDateString("en-US", options).toUpperCase();
};

export default function NewsAdmin() {
    const [newsList, setNewsList] = useState([]);
    const [form, setForm] = useState({
        news_heading: "",
        news_content: "",
        time: "",
        link: "",
        button_name: "Read the article",
        tags: "",
        media_type: "image", // Default to image
        image: null,
        youtube_url: "",
    });

    const [editingId, setEditingId] = useState(null);
    const [ReactQuill, setReactQuill] = useState(null);

    useEffect(() => {
        // Dynamically import ReactQuill only on client
        import("react-quill").then((module) => {
            setReactQuill(() => module.default);
        });
    }, []);

    const fetchNews = async () => {
        try {
            const res = await fetch("https://app.akiya2.com/all_news");
            const data = await res.json();

            const cleaned = (data || []).map((item) => ({
                ...item,
                tags: item.tags || [],
                news_heading: item.news_heading?.trim(),
                news_content: item.news_content?.trim(),
                time: item.time?.trim(),
                link: item.link?.trim(),
            }));

            setNewsList(cleaned);
        } catch (err) {
            console.error("Failed to fetch news", err);
            toast.error("Failed to fetch news list.");
        }
    };

    useEffect(() => {
        fetchNews();
    }, []);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        setForm({ ...form, [name]: files ? files[0] : value });
    };

    const handleDateChange = (e) => {
        setForm({ ...form, time: e.target.value });
    };

    const handleQuillChange = (value) => {
        setForm({ ...form, news_content: value });
    };

    const handleMediaTypeChange = (e) => {
        setForm({ ...form, media_type: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData();

        // Add all form fields to formData
        Object.keys(form).forEach((key) => {
            // Skip image field if using YouTube or no image selected
            if (key === "image" && (form.media_type !== "image" || !form[key])) {
                return;
            }
            
            // Skip youtube_url if not using YouTube
            if (key === "youtube_url" && form.media_type !== "youtube") {
                return;
            }
            
            if (form[key] !== null && form[key] !== undefined) {
                if (key === "time") {
                    formData.append("time", formatDate(form.time));
                } else {
                    formData.append(key, form[key]);
                }
            }
        });

        const endpoint = editingId ? `${API_BASE}/edit/${editingId}` : `${API_BASE}/add`;
        const method = editingId ? "PUT" : "POST";

        try {
            await fetch(endpoint, {
                method,
                body: formData,
            });

            toast.success(editingId ? "News updated successfully!" : "News added successfully!");

            setForm({
                news_heading: "",
                news_content: "",
                time: "",
                link: "",
                button_name: "Read the article",
                tags: "",
                media_type: "image",
                image: null,
                youtube_url: "",
            });

            setEditingId(null);
            fetchNews();
        } catch (err) {
            console.error("Error submitting news:", err);
            toast.error("Failed to submit news.");
        }
    };

    const handleEdit = (item) => {
        const parsedDate = new Date(item.time);
        const yyyyMMdd = parsedDate.toISOString().split("T")[0];

        setEditingId(item._id);
        setForm({
            news_heading: item.news_heading,
            news_content: item.news_content,
            time: yyyyMMdd,
            link: item.link,
            button_name: item.button_name || "Read the article",
            tags: item.tags?.join(", "),
            media_type: item.media_type || "image",
            image: null,
            youtube_url: item.media_type === "youtube" ? item.coverImage : "",
        });

        toast.info("Editing news item...");
    };

    const handleDelete = async (id) => {
        try {
            await fetch(`${API_BASE}/delete/${id}`, { method: "DELETE" });
            toast.success("News deleted successfully.");
            fetchNews();
        } catch (err) {
            console.error("Delete failed:", err);
            toast.error("Failed to delete news.");
        }
    };

    // Helper function to render different media types
    const renderMedia = (item) => {
        if (!item.coverImage) return null;
        
        if (item.media_type === "youtube") {
            // Extract video ID from YouTube URL
            const getYouTubeID = (url) => {
                const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
                const match = url.match(regExp);
                return (match && match[2].length === 11) ? match[2] : null;
            };
            
            const videoId = getYouTubeID(item.coverImage);
            if (videoId) {
                return (
                    <div className="mt-2">
                        <p className="text-sm text-gray-500 mb-1">YouTube Video:</p>
                        <iframe 
                            width="280" 
                            height="157" 
                            src={`https://www.youtube.com/embed/${videoId}`} 
                            title="YouTube video" 
                            frameBorder="0" 
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                            allowFullScreen
                        ></iframe>
                    </div>
                );
            }
            return <p className="text-sm text-red-500">Invalid YouTube URL</p>;
        } else if (item.media_type === "video") {
            return (
                <div className="mt-2">
                    <p className="text-sm text-gray-500 mb-1">Video:</p>
                    <video controls width="280" className="rounded">
                        <source src={item.coverImage} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                </div>
            );
        } else {
            // Default to image
            return (
                <div className="mt-2">
                    <img src={item.coverImage} alt="cover" className="w-32 rounded" />
                </div>
            );
        }
    };

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <h1 className="text-2xl font-bold mb-4">{editingId ? "Edit News" : "Add News"}</h1>

            <form onSubmit={handleSubmit} className="space-y-4 bg-white shadow p-4 rounded">
                {[
                    { name: "news_heading", label: "News Heading" },
                    { name: "link", label: "External Link (if any)" },
                    { name: "button_name", label: "Custom Button Text" },
                    { name: "tags", label: "Tags (comma-separated)" },
                ].map(({ name, label }) => (
                    <div key={name} className="flex flex-col space-y-1">
                        <label htmlFor={name} className="font-medium text-gray-700">
                            {label}
                        </label>
                        <input
                            name={name}
                            id={name}
                            value={form[name]}
                            onChange={handleChange}
                            className="w-full border p-2 rounded"
                            required={name !== "button_name"}
                        />
                    </div>
                ))}

                {/* Date Picker */}
                <div className="flex flex-col space-y-1">
                    <label htmlFor="time" className="font-medium text-gray-700">
                        Date
                    </label>
                    <input
                        type="date"
                        name="time"
                        id="time"
                        value={form.time}
                        onChange={handleDateChange}
                        className="w-full border p-2 rounded"
                        required
                    />
                </div>

                {/* Quill Editor */}
                <label className="block mt-2 font-medium">News Content</label>
                {ReactQuill ? (
                    <ReactQuill
                        theme="snow"
                        value={form.news_content}
                        onChange={handleQuillChange}
                        className="bg-white rounded"
                    />
                ) : (
                    <p>Loading editor...</p>
                )}

                {/* Media Type Selection */}
                <div className="flex flex-col space-y-1">
                    <label htmlFor="media_type" className="font-medium text-gray-700">
                        Media Type
                    </label>
                    <select
                        name="media_type"
                        id="media_type"
                        value={form.media_type}
                        onChange={handleMediaTypeChange}
                        className="w-full border p-2 rounded"
                    >
                        <option value="image">Image</option>
                        <option value="youtube">YouTube Video</option>
                        <option value="video">Upload Video</option>
                    </select>
                </div>

                {/* Conditional Media Input */}
                {form.media_type === "image" && (
                    <div className="flex flex-col space-y-1">
                        <label htmlFor="image" className="font-medium text-gray-700">
                            Upload Image
                        </label>
                        <input 
                            type="file" 
                            name="image" 
                            id="image"
                            accept="image/*"
                            onChange={handleChange} 
                            className="w-full" 
                        />
                    </div>
                )}

                {form.media_type === "youtube" && (
                    <div className="flex flex-col space-y-1">
                        <label htmlFor="youtube_url" className="font-medium text-gray-700">
                            YouTube URL
                        </label>
                        <input
                            type="url"
                            name="youtube_url"
                            id="youtube_url"
                            value={form.youtube_url}
                            onChange={handleChange}
                            placeholder="https://www.youtube.com/watch?v=..."
                            className="w-full border p-2 rounded"
                            required={form.media_type === "youtube"}
                        />
                    </div>
                )}

                {form.media_type === "video" && (
                    <div className="flex flex-col space-y-1">
                        <label htmlFor="image" className="font-medium text-gray-700">
                            Upload Video
                        </label>
                        <input 
                            type="file" 
                            name="image" 
                            id="image"
                            accept="video/*"
                            onChange={handleChange} 
                            className="w-full" 
                        />
                    </div>
                )}

                <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded">
                    {editingId ? "Update News" : "Add News"}
                </button>
            </form>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold mb-2">All News</h2>
            <div className="space-y-4">
                {newsList.map((item) => (
                    <div
                        key={item._id}
                        className="p-4 border rounded shadow-sm flex justify-between items-start"
                    >
                        <div className="w-full">
                            <h3 className="font-bold">{item.news_heading}</h3>
                            <p className="text-sm text-gray-600">{item.time}</p>
                            <div className="mt-2" dangerouslySetInnerHTML={{ __html: item.news_content }}></div>
                            
                            {/* Display media based on type */}
                            {renderMedia(item)}
                            
                            <div className="text-sm mt-1 text-gray-500">{item.tags?.join(", ")}</div>
                        </div>
                        <div className="space-x-2 flex">
                            <button
                                className="bg-yellow-400 text-white px-3 py-1 rounded"
                                onClick={() => handleEdit(item)}
                            >
                                Edit
                            </button>
                            <button
                                className="bg-red-500 text-white px-3 py-1 rounded"
                                onClick={() => handleDelete(item._id)}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}